import {
    useEffect,
    useState,
    useMemo,
    useRef,
    type FC
} from "react";
import {
    Animated,
    Platform,
    View
} from "react-native";
import type IBadgeCounterProps from "./type";
import type {
    BadgeCounterCharacterType
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    getMotionDuration,
    useReducedMotion,
    MOTION_EASINGS
} from "../../../../helpers/motion";
import {
    FIXED_BOX_MAX_FONT_SCALE
} from "../../../../helpers/theme/typography";
import Text from "../../../text";

const BADGE_COUNTER_SPACE = "\u00A0";

const BadgeCounterCharacter: FC<BadgeCounterCharacterType> = ({
    titleVariant,
    customColor,
    customTheme,
    titleStyle,
    character,
    direction
}) => {
    const {
        typography,
        durations
    } = NCoreUIKitTheme.useContext(customTheme);

    const isReduceMotionEnabled = useReducedMotion();

    const progress = useRef(new Animated.Value(1)).current;

    const currentCharacterRef = useRef(character);

    const [
        previousCharacter,
        setPreviousCharacter
    ] = useState<string | null>(null);

    const lineHeight = typography[titleVariant].lineHeight;

    const offset = direction * lineHeight;

    const {
        character: characterDynamicStyle
    } = useStyles({
        lineHeight
    });

    const animatedStyles = useMemo(() => ({
        current: {
            transform: [
                {
                    translateY: progress.interpolate({
                        outputRange: [
                            offset,
                            0
                        ],
                        inputRange: [
                            0,
                            1
                        ]
                    })
                }
            ],
            opacity: progress
        },
        previous: {
            transform: [
                {
                    translateY: progress.interpolate({
                        outputRange: [
                            0,
                            -offset
                        ],
                        inputRange: [
                            0,
                            1
                        ]
                    })
                }
            ],
            opacity: progress.interpolate({
                outputRange: [
                    1,
                    0
                ],
                inputRange: [
                    0,
                    1
                ]
            })
        }
    }), [
        progress,
        offset
    ]);

    useEffect(() => {
        if(currentCharacterRef.current === character) {
            return;
        }

        const previous = currentCharacterRef.current;

        currentCharacterRef.current = character;

        const duration = getMotionDuration({
            isReduceMotionEnabled,
            duration: "medium",
            durations
        });

        if(!duration || direction === 0) {
            setPreviousCharacter(null);

            progress.setValue(1);

            return;
        }

        setPreviousCharacter(previous);

        progress.setValue(0);

        const animation = Animated.timing(progress, {
            useNativeDriver: Platform.OS !== "web",
            easing: MOTION_EASINGS.standard,
            toValue: 1,
            duration
        });

        animation.start(({
            finished
        }) => {
            if(finished) {
                setPreviousCharacter(null);
            }
        });

        return () => {
            animation.stop();
        };
    }, [
        character
    ]);

    const renderText = (value: string) => <Text
        maxFontSizeMultiplier={FIXED_BOX_MAX_FONT_SCALE}
        customColor={customColor}
        customTheme={customTheme}
        variant={titleVariant}
        style={[
            titleStyle,
            stylesheet.title
        ]}
        numberOfLines={1}
    >
        {value}
    </Text>;

    if(previousCharacter === null) {
        return <View
            style={[
                stylesheet.character,
                characterDynamicStyle
            ]}
        >
            {renderText(character)}
        </View>;
    }

    return <View
        style={[
            stylesheet.character,
            characterDynamicStyle
        ]}
    >
        <Animated.View
            style={[
                stylesheet.previous,
                animatedStyles.previous
            ]}
        >
            {renderText(previousCharacter)}
        </Animated.View>
        <Animated.View
            style={animatedStyles.current}
        >
            {renderText(character)}
        </Animated.View>
    </View>;
};

const BadgeCounter: FC<IBadgeCounterProps> = ({
    titleVariant,
    customColor,
    customTheme,
    titleStyle,
    direction,
    title
}) => {
    const characters = Array.from(title).map((character) => character === " " ? BADGE_COUNTER_SPACE : character);

    const webDirectionProps = Platform.OS === "web" ? {
        dir: "ltr"
    } : {};

    return <View
        {...webDirectionProps}
        style={stylesheet.counter}
    >
        {
            characters.map((character, index) => <BadgeCounterCharacter
                key={characters.length - index}
                titleVariant={titleVariant}
                customColor={customColor}
                customTheme={customTheme}
                titleStyle={titleStyle}
                character={character}
                direction={direction}
            />)
        }
    </View>;
};
export default BadgeCounter;
